import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function CmsManager() {
  const [hero, setHero] = useState(null);
  const [faqs, setFaqs] = useState(null);
  const [nf, setNf] = useState({ category: "Booking", question: "", answer: "" });

  const load = () => {
    api.get("/cms/home").then(({ data }) => setHero(data && data.id ? data : { id: "home", hero_title: "", hero_subtitle: "" }));
    api.get("/admin/faqs").then(({ data }) => setFaqs(data));
  };
  useEffect(() => { load(); }, []);

  const saveHero = async () => { await api.put("/admin/cms/home", hero); toast.success("Homepage content saved"); };
  const addFaq = async () => { if (!nf.question) return toast.error("Enter a question"); await api.post("/admin/faqs", nf); setNf({ category: "Booking", question: "", answer: "" }); toast.success("FAQ added"); load(); };
  const delFaq = async (id) => { await api.delete(`/admin/faqs/${id}`); toast.success("FAQ removed"); load(); };
  const saveFaq = async (f) => { await api.patch(`/admin/faqs/${f.id}`, { question: f.question, answer: f.answer, category: f.category }); toast.success("FAQ updated"); };

  if (!hero || !faqs) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading font-semibold text-navy">Homepage Hero</h3>
        <div className="grid gap-3">
          <div><Label>Hero title</Label><Input value={hero.hero_title || ""} onChange={(e) => setHero({ ...hero, hero_title: e.target.value })} data-testid="cms-hero-title" /></div>
          <div><Label>Hero subtitle</Label><Textarea value={hero.hero_subtitle || ""} onChange={(e) => setHero({ ...hero, hero_subtitle: e.target.value })} data-testid="cms-hero-subtitle" /></div>
        </div>
        <Button className="mt-4 gap-2 bg-brand hover:bg-brand/90" onClick={saveHero} data-testid="cms-save-hero"><Save size={15} /> Save Homepage</Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading font-semibold text-navy">FAQs</h3>
        <div className="mb-4 grid gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-[140px_1fr_auto]">
          <Input placeholder="Category" value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} data-testid="faq-new-category" />
          <div className="grid gap-2">
            <Input placeholder="Question" value={nf.question} onChange={(e) => setNf({ ...nf, question: e.target.value })} data-testid="faq-new-question" />
            <Input placeholder="Answer" value={nf.answer} onChange={(e) => setNf({ ...nf, answer: e.target.value })} data-testid="faq-new-answer" />
          </div>
          <Button className="gap-1 bg-navy hover:bg-navy/90" onClick={addFaq} data-testid="faq-add"><Plus size={15} /> Add</Button>
        </div>
        <div className="space-y-3">
          {faqs.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 p-3" data-testid={`faq-item-${f.id}`}>
              <Input className="mb-2 font-medium" value={f.question} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, question: e.target.value } : x))} />
              <Textarea value={f.answer} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, answer: e.target.value } : x))} />
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => saveFaq(f)}><Save size={13} /> Save</Button>
                <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={() => delFaq(f.id)} data-testid={`faq-del-${f.id}`}><Trash2 size={13} /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SettingsManager() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
  const F = ({ k, label, type = "text" }) => <div><Label>{label}</Label><Input type={type} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`set-${k}`} /></div>;
  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading font-semibold text-navy">Company Profile</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <F k="company_name" label="Company name" />
          <F k="currency" label="Currency" />
          <F k="support_email" label="Support email" />
          <F k="support_phone" label="Support phone" />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading font-semibold text-navy">Commission & Policy</h3>
        <div className="grid gap-3">
          <F k="commission_pct" label="Platform commission (%)" type="number" />
          <div><Label>Cancellation policy</Label><Textarea value={s.cancellation_policy || ""} onChange={(e) => setS({ ...s, cancellation_policy: e.target.value })} data-testid="set-cancellation" /></div>
        </div>
      </div>
      <Button className="gap-2 bg-brand hover:bg-brand/90" onClick={save} data-testid="settings-save"><Save size={15} /> Save Settings</Button>
    </div>
  );
}
